import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';
import { formatViews } from '@/lib/videos';

interface VideoCardProps {
  video: VideoItem;
  index?: number;
  showTags?: boolean;
}

export default function VideoCard({ video, index = 0, showTags = false }: VideoCardProps) {
  return (
    <Link
      to={`/detail/${video.id}`}
      className="group block animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 9) * 55}ms` }}
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-primary-950">
        <img
          src={video.cover}
          alt={`${video.title} - 日韩自拍视频封面`}
          title={`${video.title} - ${video.category}高清视频`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/10 to-primary-950/10" />

        <span className="absolute left-2.5 top-2.5 flex items-center gap-1 rounded bg-background-50/92 px-1.5 py-0.5 text-[10px] font-medium text-foreground-900">
          {video.resolution}
        </span>
        <span className="absolute right-2.5 top-2.5 rounded bg-primary-950/75 px-1.5 py-0.5 text-[10px] text-background-100">
          {video.duration}
        </span>

        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 text-primary-950">
            <i className="ri-play-fill text-xl" />
          </span>
        </span>

        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-2.5 pb-2">
          <span className="flex items-center gap-1 text-[10px] text-background-200">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-eye-line text-[11px]" />
            </span>
            {formatViews(video.views)}
          </span>
          <span className="rounded bg-background-50/15 px-1.5 py-0.5 text-[10px] text-background-100 backdrop-blur">
            {video.region}
          </span>
        </div>
      </div>

      <h3 className="mt-2.5 line-clamp-2 text-[14px] font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-700">
        {video.title}
      </h3>

      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-500">
        <span className="text-primary-600">{video.category}</span>
        <span className="text-background-400">/</span>
        <span>{video.year}</span>
        <span className="text-background-400">/</span>
        <span>{video.perspective}</span>
      </div>

      {showTags && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {video.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">
              {tag}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}